<mat-expansion-panel
  class="!overflow-hidden !rounded-2xl !border !border-gray-6 !bg-surface-default !shadow-sm transition-all duration-200 hover:!border-gray-7 !before:hidden relative"
  [expanded]="expanded()"
  (expandedChange)="expanded.set($event)"
>
  <!-- Subtle decorative ambient glow -->
  <div
    class="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-orange-a2 blur-3xl"
  ></div>

  <!-- Card Header -->
  <mat-expansion-panel-header
    class="!h-auto !py-4.5 !px-6 hover:!bg-gray-2/40 transition-colors [&_.mat-content]:!flex-row [&_.mat-content]:!items-center [&_.mat-content]:!justify-between [&_.mat-content]:!w-full [&_.mat-content]:!m-0 [&_.mat-content]:!overflow-visible"
  >
    <div class="flex items-center justify-between w-full gap-4 mr-3">
      <div class="flex items-center gap-3.5 min-w-0">
        <div
          class="h-11 w-11 flex items-center justify-center rounded-xl bg-orange-a2 text-orange-11 border border-orange-a4 shadow-xs shrink-0"
        >
          <mat-icon class="text-2xl leading-none">apartment</mat-icon>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="text-base sm:text-lg font-bold text-gray-12 m-0">Permis de construire et urbanisme</h4>
          </div>
          <p class="text-xs sm:text-sm text-gray-11 m-0 mt-0.5 truncate hidden sm:block">
            Dynamique de construction neuve et programmes autorisés dans le secteur.
          </p>
        </div>
      </div>

      <div class="shrink-0">
        @if (pressureInfo(); as pressure) {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3.5 py-1.5 rounded-full border shadow-xs"
            [class]="pressure.classes"
          >
            <span>{{ pressure.label }}</span>
          </div>
        } @else {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1 rounded-full border border-gray-6 bg-gray-2 text-gray-11 shadow-2xs"
          >
            <mat-icon fontIcon="hourglass_empty" class="text-xs leading-none shrink-0"></mat-icon>
            <span>Consultation en cours</span>
          </div>
        }
      </div>
    </div>
  </mat-expansion-panel-header>

  <div class="px-6 pb-6 pt-3 border-t border-gray-5">
    @if (sitadel(); as data) {
      <!-- Main Content -->
      <div class="relative mt-5 flex flex-col gap-5">
        <!-- 3-Tier Pressure Level Ladder -->
        <div class="rounded-xl border border-gray-5 bg-gray-2/70 p-4 sm:p-5">
          <div class="flex items-center justify-between text-sm text-gray-11 mb-2.5 font-medium">
            <span class="font-semibold text-gray-12">Intensité de constructibilité neuve</span>
            <span class="inline-flex items-center gap-1.5 text-xs text-gray-11">
              <mat-icon fontIcon="radar" class="text-sm text-orange-11 leading-none"></mat-icon>
              Rayon de {{ data.radiusMeters || 500 }} m
            </span>
          </div>

          <div class="grid grid-cols-3 gap-2">
            @for (
              step of [
                {
                  label: 'Pression faible',
                  desc: 'Cadre stable, peu de chantiers',
                  border: 'border-jade-a4',
                  text: 'text-jade-11',
                },
                {
                  label: 'Pression modérée',
                  desc: 'Développement équilibré',
                  border: 'border-blue-a4',
                  text: 'text-blue-11',
                },
                {
                  label: 'Pression élevée',
                  desc: 'Forte mutation urbaine',
                  border: 'border-orange-a4',
                  text: 'text-orange-11',
                },
              ];
              track step.label;
              let i = $index
            ) {
              <div
                class="relative flex flex-col items-center justify-center rounded-lg p-3 text-center transition-all duration-300 border"
                [class]="
                  i === pressureStepIndex()
                    ? 'bg-surface-default shadow-xs border-2 ' + step.border
                    : 'bg-gray-3/60 border-transparent opacity-65'
                "
              >
                <span
                  class="text-xs sm:text-sm font-bold"
                  [class]="i === pressureStepIndex() ? step.text : 'text-gray-11'"
                >
                  {{ step.label }}
                </span>
                <span class="hidden sm:inline-block text-xs text-gray-10 mt-0.5">{{ step.desc }}</span>
                @if (i === pressureStepIndex()) {
                  <span
                    class="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-12 text-[10px] text-white font-bold"
                    >✓</span
                  >
                }
              </div>
            }
          </div>
        </div>

        <!-- 3 Metrics Tiles -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <!-- 1. Units under construction -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Logements autorisés ou en chantier</span>
                <mat-icon fontIcon="home_work" class="text-sm leading-none text-gray-9"></mat-icon>
              </div>
              <div class="mt-2 flex items-baseline gap-1.5">
                <span class="text-2xl sm:text-3xl font-black tracking-tight text-gray-12">
                  {{ data.unitsUnderConstruction ?? 0 }}
                </span>
                <span class="text-sm font-bold text-gray-11">logements neufs</span>
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              Volume total d'unités d'habitation autorisées par les permis récents.
            </p>
          </div>

          <!-- 2. Permits Count -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Permis de construire enregistrés</span>
                <mat-icon fontIcon="description" class="text-sm leading-none text-gray-9"></mat-icon>
              </div>
              <div class="mt-2 flex items-baseline gap-1.5">
                <span class="text-2xl sm:text-3xl font-black tracking-tight text-gray-12">
                  {{ data.permitsCount ?? 0 }}
                </span>
                <span class="text-sm font-bold text-gray-11">permis déposés</span>
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              Nombre de dossiers d'urbanisme instruits et validés dans la zone.
            </p>
          </div>

          <!-- 3. Qualitative Insight -->
          <div
            class="rounded-xl border border-gray-6 bg-gray-2/80 p-5 flex flex-col justify-between backdrop-blur-xs"
          >
            <div>
              <div class="flex items-center justify-between text-sm font-medium text-gray-11 mb-1">
                <span>Contexte urbain</span>
                <mat-icon fontIcon="insights" class="text-sm leading-none text-gray-9"></mat-icon>
              </div>
              <div class="mt-2">
                <span class="text-base font-bold text-gray-12">
                  {{ pressureInfo()?.label || 'Information en cours' }}
                </span>
              </div>
            </div>
            <p class="mt-3 text-xs sm:text-sm text-gray-11 m-0 leading-relaxed">
              {{
                pressureInfo()?.description ||
                  'Impact direct sur la concurrence de l’offre neuve et la rareté du foncier.'
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Micro footer note -->
      <div class="mt-4 flex items-center gap-2 text-xs text-gray-11 pt-2">
        <mat-icon fontIcon="info_outline" class="text-sm text-orange-11 leading-none shrink-0"></mat-icon>
        <span
          >Données issues de la base officielle Sitadel du Ministère de la Transition écologique et de la
          Cohésion des territoires.</span
        >
      </div>
    } @else {
      <!-- Empty / Pending State -->
      <div
        class="mt-4 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-6 py-8 text-center bg-gray-2/50"
      >
        <div class="h-10 w-10 rounded-full bg-gray-3 flex items-center justify-center text-gray-10">
          <mat-icon class="text-xl">sync_disabled</mat-icon>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-12 m-0">Données Sitadel en cours de consultation</p>
          <p class="text-sm text-gray-11 m-0 mt-1 max-w-sm">
            Les permis de construire et autorisations d'urbanisme à 500 m sont en cours de consolidation.
          </p>
        </div>
      </div>
    }
  </div>
</mat-expansion-panel>
